import React, { useState, useEffect } from 'react';
import { X, Lock, Paperclip, AlertTriangle } from 'lucide-react';

const CreateAssignmentModal = ({
  show,
  onClose,
  onCreate,
  classes = [],
  subjects = [],
  teachers = []
}) => {
  const [academicYear, setAcademicYear] = useState('2026-2027');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('A');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [resolvedTeacher, setResolvedTeacher] = useState(null);
  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [assignedDate, setAssignedDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState(new Date(Date.now() + 86400000 * 3).toISOString().split('T')[0]);
  const [fileName, setFileName] = useState('');

  // Extract available unique class names
  const availableClasses = Array.from(new Set(classes.map(c => c.className || c.name).filter(Boolean)));

  useEffect(() => {
    if (show) {
      if (availableClasses.length > 0 && !selectedClass) {
        setSelectedClass(availableClasses[0]);
      }
    }
  }, [show, availableClasses]);

  // Auto-resolve Teacher whenever Subject or Class changes
  useEffect(() => {
    if (!selectedSubject) {
      setResolvedTeacher(null);
      return;
    }

    // 1. Check matching subject item
    const subObj = subjects.find(s => s.subjectName === selectedSubject || s.subjectCode === selectedSubject);
    let tName = subObj?.teacher || subObj?.teacherName || subObj?.assignedTeacher;

    // 2. If no direct teacher in subject, match by department / specialization in teachers list
    if (!tName && teachers && teachers.length > 0) {
      const match = teachers.find(t => {
        const dept = (t.department || t.specialization || '').toLowerCase();
        const sName = selectedSubject.toLowerCase();
        return dept && (sName.includes(dept) || dept.includes(sName.split(' ')[0]));
      });
      if (match) {
        tName = match.teacherName || match.name || match.fullName;
      }
    }

    // Fallback: pick first real teacher from DB if available, else 'Faculty'
    if (!tName) {
      if (teachers && teachers.length > 0) {
        tName = teachers[0].teacherName || teachers[0].name || teachers[0].fullName || 'Assigned Faculty';
      } else {
        tName = 'Faculty';
      }
    }

    setResolvedTeacher(tName);
  }, [selectedSubject, subjects, teachers]);

  if (!show) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title || !selectedSubject || !selectedClass) {
      alert('Please fill out all required fields.');
      return;
    }

    onCreate({
      id: 'ASN-' + Date.now(),
      title,
      instructions,
      academicYear,
      className: selectedClass,
      section: selectedSection,
      subject: selectedSubject,
      teacher: resolvedTeacher || 'Faculty Teacher',
      assignedDate,
      dueDate,
      fileName,
      status: 'Active',
      submittedCount: 0,
      totalStudents: 30
    });

    // Reset
    setTitle('');
    setInstructions('');
    setFileName('');
    onClose();
  };

  return (
    <div className="fs-modal-overlay" style={{ zIndex: 1200 }}>
      <div className="fs-modal-card" style={{ maxWidth: '540px', borderRadius: '16px', overflow: 'hidden' }}>
        
        {/* HEADER */}
        <div style={{
          padding: '18px 24px',
          borderBottom: '1px solid #E2E8F0',
          display: 'flex',
          justify: 'space-between',
          alignItems: 'center',
          backgroundColor: '#F8FAFC'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0F172A' }}>
              Create Homework
            </h3>
            <div style={{ fontSize: '12.5px', color: '#64748B', marginTop: '2px' }}>
              Assign homework to a class and track student submissions
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' }}>
            <X size={20} />
          </button>
        </div>

        {/* FORM BODY */}
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px', maxHeight: '75vh', overflowY: 'auto' }}>
            
            {/* Academic Year, Class, Section Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '4px', display: 'block' }}>
                  Academic Year *
                </label>
                <select
                  className="form-control"
                  value={academicYear}
                  onChange={(e) => setAcademicYear(e.target.value)}
                  style={{ height: '38px', borderRadius: '8px', fontSize: '13px', fontWeight: '600' }}
                  required
                >
                  <option value="2026-2027">2026–2027</option>
                  <option value="2025-2026">2025–2026</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '4px', display: 'block' }}>
                  Class / Grade *
                </label>
                <select
                  className="form-control"
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  style={{ height: '38px', borderRadius: '8px', fontSize: '13px', fontWeight: '600' }}
                  required
                >
                  <option value="">Select Class</option>
                  {(availableClasses.length > 0 ? availableClasses : ['6th Standard', '7th Standard', '8th Standard', '9th Standard', '10th Standard']).map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '4px', display: 'block' }}>
                  Section *
                </label>
                <select
                  className="form-control"
                  value={selectedSection}
                  onChange={(e) => setSelectedSection(e.target.value)}
                  style={{ height: '38px', borderRadius: '8px', fontSize: '13px', fontWeight: '600' }}
                  required
                >
                  {['A', 'B', 'C', 'D'].map(sec => (
                    <option key={sec} value={sec}>Section {sec}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Subject Selection */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '4px', display: 'block' }}>
                Subject *
              </label>
              <select
                className="form-control"
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                style={{ height: '40px', borderRadius: '8px', fontSize: '13.5px', fontWeight: '600' }}
                required
              >
                <option value="">Select Subject</option>
                {['Mathematics', 'English', 'Science', 'Social Studies', 'Kannada', 'Hindi', 'Computer Science', 'Physical Education (PET)'].map(sub => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </select>
            </div>

            {/* Auto-Resolved Teacher Badge (Read-Only) */}
            {selectedSubject && (
              <div style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: '#64748B', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Lock size={13} color="#64748B" /> Assigned Teacher
                </span>
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#0F172A' }}>
                  {resolvedTeacher || 'School Faculty'}
                </span>
              </div>
            )}

            {/* Title */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '4px', display: 'block' }}>
                Homework Title *
              </label>
              <input
                type="text"
                className="form-control"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Kannada Essay / Algebra Exercise 4"
                style={{ height: '40px', borderRadius: '8px', fontSize: '13.5px' }}
                required
              />
            </div>

            {/* Instructions */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '4px', display: 'block' }}>
                Instructions / Homework Details
              </label>
              <textarea
                className="form-control"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Write specific instructions or questions for students..."
                rows={3}
                style={{ borderRadius: '8px', fontSize: '13px' }}
              />
            </div>

            {/* Dates Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '4px', display: 'block' }}>
                  Assigned Date *
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={assignedDate}
                  onChange={(e) => setAssignedDate(e.target.value)}
                  style={{ height: '38px', borderRadius: '8px', fontSize: '13px' }}
                  required
                />
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '4px', display: 'block' }}>
                  Due Date *
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  style={{ height: '38px', borderRadius: '8px', fontSize: '13px' }}
                  required
                />
              </div>
            </div>

            {/* Attachment File Input */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '4px', display: 'block' }}>
                Attachment (Optional)
              </label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <label style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  backgroundColor: '#F1F5F9',
                  border: '1px solid #CBD5E1',
                  borderRadius: '8px',
                  fontSize: '12.5px',
                  fontWeight: '600',
                  color: '#334155',
                  cursor: 'pointer'
                }}>
                  <Paperclip size={15} /> Upload PDF / Document
                  <input
                    type="file"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setFileName(e.target.files[0].name);
                      }
                    }}
                  />
                </label>
                {fileName && (
                  <span style={{ fontSize: '12px', color: '#2563EB', fontWeight: '600' }}>
                    📎 {fileName}
                  </span>
                )}
              </div>
            </div>

          </div>

          {/* FOOTER */}
          <div style={{
            padding: '16px 24px',
            borderTop: '1px solid #E2E8F0',
            display: 'flex',
            justify: 'flex-end',
            gap: '10px',
            backgroundColor: '#F8FAFC'
          }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #CBD5E1',
                backgroundColor: 'white',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 20px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#2563EB',
                color: 'white',
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)'
              }}
            >
              ✓ Publish Homework
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

export default CreateAssignmentModal;
